import React, { useState, useEffect, forwardRef } from 'react'
import styled from 'styled-components'
import Input from './input'
import { colors } from '../../../styles/theme'
import Textarea from './textarea'
import Select from './select'
import { useFormValidator } from './form'
import Checkbox from './checkbox'
import Radio from './radio'
import DatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'
import PhoneInput from './phone-input'

const ValidationMessage = styled.div`
  color: ${colors.danger};
`

const validate = ({
  isTouched,
  value,
  required,
  minLength,
  maxLength,
}: {
  isTouched: boolean
  value: string
  required?: boolean
  minLength?: number
  maxLength?: number
}): {
  valid: boolean
  showError?: boolean
  message?: string
} => {
  if (required && !value) {
    return {
      valid: false,
      showError: isTouched,
      message: 'This field is required.',
    }
  }

  if (minLength !== undefined && value.length < minLength) {
    return {
      valid: false,
      showError: isTouched,
      message: `This field must be at least ${minLength} characters.`,
    }
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return {
      valid: false,
      showError: isTouched,
      message: `This field cannot be longer than ${maxLength} characters.`,
    }
  }

  return {
    valid: true,
  }
}

export const ValidatedInput = ({
  name,
  required,
  minLength,
  maxLength,
  value,
  onBlur = () => {},
  ...props
}: {
  name: string
  required?: boolean
  minLength?: number
  maxLength?: number
  value: string
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void
} & React.InputHTMLAttributes<HTMLInputElement>) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, showError, message } = validate({
    isTouched,
    required,
    minLength,
    maxLength,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleBlur = (event: React.FormEvent<HTMLInputElement>) => {
    setIsTouched(true)
    onBlur(event)
  }

  return (
    <>
      <Input
        name={name}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        onBlur={handleBlur}
        value={value}
        invalid={showError}
        {...props}
      />
      {showError && <ValidationMessage>{message}</ValidationMessage>}
    </>
  )
}

export const ValidatedPhoneNumber = ({
  name,
  onValueChange,
  required,
  value,
  onBlur = () => {},
  ...props
}: {
  name: string
  onValueChange: ({
    formattedValue,
    value,
    floatValue,
  }: {
    formattedValue: string
    value: string
    floatValue: number
  }) => void
  required?: boolean
  value: string
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void
} & React.InputHTMLAttributes<HTMLInputElement>) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, showError, message } = validate({
    isTouched,
    required,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleBlur = (event: React.FormEvent<HTMLInputElement>) => {
    setIsTouched(true)
    onBlur(event)
  }

  return (
    <>
      <PhoneInput
        name={name}
        required={required}
        onValueChange={onValueChange}
        onBlur={handleBlur}
        value={value}
        invalid={showError}
        {...props}
      />
      {showError && <ValidationMessage>{message}</ValidationMessage>}
    </>
  )
}

const DateInput = forwardRef((props: any, ref) => {
  return <Input {...props} />
})
export const ValidatedDatePicker = ({
  name,
  required,
  value,
  onBlur = () => {},
  ...props
}: {
  name: string
  required?: boolean
  value: string
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void
} & any) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, showError, message } = validate({
    isTouched,
    required,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleBlur = (event: React.FormEvent<HTMLInputElement>) => {
    setIsTouched(true)
    onBlur(event)
  }

  return (
    <>
      <DatePicker
        name={name}
        required={required}
        selected={value}
        onBlur={handleBlur}
        customInput={<DateInput name={name} required={required} value={value} />}
        {...props}
      />
      {showError && <ValidationMessage>{message}</ValidationMessage>}
    </>
  )
}

export const ValidatedTextarea = ({
  name,
  required,
  minLength,
  maxLength,
  value,
  onBlur = () => {},
  ...props
}: {
  name: string
  required?: boolean
  minLength?: number
  maxLength?: number
  value: string
  onBlur?: (event: React.FormEvent<HTMLTextAreaElement>) => void
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, showError, message } = validate({
    isTouched,
    required,
    minLength,
    maxLength,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleBlur = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setIsTouched(true)
    onBlur(event)
  }

  return (
    <>
      <Textarea
        name={name}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        onBlur={handleBlur}
        value={value}
        /*
        // @ts-ignore */
        invalid={showError}
        {...props}
      />
      {showError && <ValidationMessage>{message}</ValidationMessage>}
    </>
  )
}

export const ValidatedSelect = ({
  name,
  required,
  value,
  onBlur = () => {},
  children,
  ...props
}: {
  name: string
  required?: boolean
  value: string
  onBlur?: (event: React.FormEvent<HTMLSelectElement>) => void
  children: any
} & React.SelectHTMLAttributes<HTMLSelectElement>) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, showError, message } = validate({
    isTouched,
    required,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleBlur = (event: React.FormEvent<HTMLSelectElement>) => {
    setIsTouched(true)
    onBlur(event)
  }

  return (
    <>
      <Select
        name={name}
        required={required}
        value={value}
        invalid={showError}
        onBlur={handleBlur}
        {...props}
      >
        {children}
      </Select>
      {showError && <ValidationMessage>{message}</ValidationMessage>}
    </>
  )
}

export const ValidatedRadio = ({
  name,
  required,
  checked,
  children,
  ...props
}: {
  name: string
  required?: boolean
  checked: boolean
  children: React.ReactNode
} & React.InputHTMLAttributes<HTMLInputElement>) => {
  const valid = !required || checked
  const { putError, removeError } = useFormValidator()

  useEffect(() => {
    if (!valid) {
      putError(name, 'This field is required.')
    }
  }, [])

  if (valid) {
    removeError(name)
  }

  return (
    <Radio name={name} checked={checked} required={required} {...props}>
      {children}
    </Radio>
  )
}

export const ValidatedCheckbox = ({
  name,
  required,
  value,
  onChange = () => {},
  ...props
}: {
  name: string
  required?: boolean
  value: boolean
  onChange?: (event: any) => void
} & any) => {
  const [isTouched, setIsTouched] = useState(false)
  const { valid, message } = validate({
    isTouched,
    required,
    value,
  })

  const { putError, removeError } = useFormValidator()
  if (valid) {
    removeError(name)
  } else {
    putError(name, message!)
  }

  const handleChange = (event: any) => {
    setIsTouched(true)
    onChange(event)
  }

  return <Checkbox name={name} onChange={handleChange} required={required} {...props} />
}
